import React, { useCallback, useEffect, useState } from 'react';
import { FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDateTime } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

export default function ActivityLogs() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ page: 1, search: '', activity: '', per_page: 100 });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.activityLogs.list(filters);
      setRows(data.logs?.data || []);
      setMeta(data.logs ? { current_page: data.logs.current_page, last_page: data.logs.last_page, total: data.logs.total, per_page: data.logs.per_page, per_page_options: data.logs.per_page_options } : null);
      if (data.filters && data.filters.activities) setActivities(data.filters.activities);
    } catch (err) {
      showError('Failed to load activity logs', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`log #${row.id}`);
    if (!res.isConfirmed) return;
    try {
      await api.activityLogs.remove(row.id);
      toast('success', '', 'Log deleted');
      load();
    } catch (err) {
      showError('Failed to delete log', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Activity Logs"
        subtitle="Every action performed in the system."
        crumbs={[{ label: 'System' }, { label: 'Activity Logs' }]}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search description..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect options={[{ value: '', label: 'All Activities' }, ...(activities.length ? activities : ['CREATED', 'UPDATED', 'DELETED', 'LOGIN', 'LOGOUT', 'POSTED', 'VOIDED', 'APPROVED', 'PAYROLL_GENERATED', 'PAYROLL_PROCESSED']).map((a) => ({ value: a, label: a }))]} value={filters.activity} onChange={(v) => setFilters({ ...filters, activity: v, page: 1 })} placeholder="All Activities" />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="activity_logs" label="activity logs" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No activity logs found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'created_at', label: 'Timestamp', render: (r) => formatDateTime(r.created_at) },
              { key: 'user', label: 'User', render: (r) => (r.user ? r.user.name : '-') },
              { key: 'activity', label: 'Activity', render: (r) => <StatusBadge status={r.activity} /> },
              { key: 'description', label: 'Description' },
              { key: 'ip_address', label: 'IP Address' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setFilters({ ...filters, page: p })} onPerPage={(n) => setFilters({ ...filters, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}